import React, { useState } from "react";
import { createParameterRequest } from "../../api/ParameterRequestAPI";
import { getUserMessage, logApiError } from "../../api/apiError";

interface Props {
    token: string;
    categoryId: string;
    categoryName?: string;
}

/**
 * Lets a user ask for a parameter the category is missing. Free text: the
 * parameter's name, what it means, and its possible values.
 */
const ParameterRequestPanel: React.FC<Props> = ({ token, categoryId, categoryName }) => {
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [okMsg, setOkMsg] = useState<string | null>(null);

    const submit = async () => {
        if (text.trim().length < 3) return;
        setBusy(true);
        setError(null);
        setOkMsg(null);
        try {
            await createParameterRequest(token, categoryId, text.trim());
            setOkMsg("Request submitted — thank you!");
            setText("");
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <details style={{ marginTop: "1rem" }}>
            <summary style={{ cursor: "pointer", color: "var(--text-secondary)", fontSize: "0.9rem" }}>
                Missing a parameter{categoryName ? ` for "${categoryName}"` : ""}? Request one
            </summary>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "0.5rem", maxWidth: "40rem" }}>
                <textarea
                    value={text}
                    onChange={(e) => { setText(e.target.value); setOkMsg(null); }}
                    rows={3}
                    placeholder={'Describe the parameter: its name, what it means, and possible values.\ne.g. "Handle material — one of: Wood, Fiberglass, Steel"'}
                    disabled={busy}
                />
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <button type="button" onClick={submit} disabled={busy || text.trim().length < 3}>
                        {busy ? "Submitting…" : "Submit request"}
                    </button>
                    {okMsg && <span className="success-message" style={{ color: "var(--accent, #2e7d32)" }}>{okMsg}</span>}
                </div>
                {error && <div className="error-message">{error}</div>}
            </div>
        </details>
    );
};

export default ParameterRequestPanel;